import React, { ReactNode, useEffect, useState } from 'react';
import { LoadingPage } from '../../Components/LoadingOverlay';
import { UnexpectedError } from '../Shared';
import { ContractDashboard } from './components/ContractDashboard';
import { NoContractAlert } from './components/NoContractAlert';
import { Error } from '../Provider/Availability/Styles'; // Find better Error component
import { Text } from '../../globalStyles';
import { CareType, ContractScope, ListContractsQuery } from '../../graphQL';
import styled from 'styled-components';
import { AddEditContractModal } from './components/AddEditContractModal';
import moment from 'moment-timezone';
import { useContractsPage } from './hooks/useContractsPage';
import { FinalButton } from '../../Components/FinalButton';
import { Select } from '../../Components/Form';

export type ContractPeriodSubset = Pick<ContractScope, 'startDate' | 'endDate'>;

export const ContractsPage = () => {
  const [selectedContractId, setSelectContractId] = useState<number | undefined>();
  const contractHookOutput = useContractsPage(selectedContractId);

  const contractId = 'data' in contractHookOutput ? contractHookOutput.data?.contractId : undefined;

  useEffect(() => {
    if (contractId !== undefined && selectedContractId === undefined) {
      setSelectContractId(contractId);
    }
  }, [contractId]);

  if (contractHookOutput.error) {
    const errorString = contractHookOutput.errorList.map(error => error?.message).toString();
    return <UnexpectedError message={errorString} />;
  }

  if (contractHookOutput.loading) {
    return <LoadingPage />;
  }

  const data = contractHookOutput.data;
  const contractsList = data.orgContractsList;
  const contractData = data.contractData;
  const careTypes = data.organization.careTypes;
  const refetchContractData = data.refetchContractData;
  const refetchContractList = data.refetchContractList;

  // Handle 0 contracts case
  if (!contractId) {
    return (
      <ContractPageWrapper
        careTypes={careTypes}
        onSelectContract={setSelectContractId}
        contractId={contractId}
        onCreateContract={refetchContractList}
      >
        <NoContractAlert />
      </ContractPageWrapper>
    );
  }

  // 0 contract case handled, AND no contract data? Error.
  if (!contractData || !careTypes) {
    return <Error>Could not determine contract data</Error>;
  }

  const contract = contractData.getContract;

  // Handle getContract state errors / type narrow for contract
  const { __typename: responseType } = contract;
  if (responseType === 'GetContractError') {
    // TODO: Handle codes individually here as necessary
    return <Error>{contract.message}</Error>;
  }

  return (
    <ContractPageWrapper
      contractListData={contractsList}
      onSelectContract={setSelectContractId}
      contractId={contractId}
      onCreateContract={refetchContractList}
      careTypes={careTypes}
    >
      <ContractDashboard
        careTypes={careTypes}
        contract={contract}
        editable={true} // We think we will need this functionality later
        onChangeContract={refetchContractData}
        contractRefetch={refetchContractData}
      />
    </ContractPageWrapper>
  );
};

const ContractPageWrapper = ({
  children,
  careTypes,
  contractListData,
  onSelectContract,
  onCreateContract,
  contractId,
}: {
  children: ReactNode;
  careTypes?: CareType[];
  contractListData?: ListContractsQuery['listContracts']['contracts'];
  onSelectContract: (newContractId: number | undefined) => void;
  onCreateContract: () => void;
  contractId: number | undefined;
}): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div style={{ minWidth: '1050px' }}>
      <ContractPageWrapperContent className="relative mb3">
        <div>
          <Text.h2 className="mb3">Care Ops Configuration</Text.h2>

          <Text.body className="mb4">
            <span className="b">Model Type: </span> Sessions Model
          </Text.body>
        </div>
        <ContractListAndAddButton>
          <ContractSelect
            contractList={contractListData}
            onValueChange={onSelectContract}
            value={contractId}
          />
          <FinalButton className="ml2" kind="primary" pill onClick={() => setIsOpen(true)}>
            Add Contract
          </FinalButton>

          <AddEditContractModal
            careTypes={careTypes}
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            onSave={selectedId => {
              onSelectContract(selectedId);
              onCreateContract();
            }}
          />
        </ContractListAndAddButton>
      </ContractPageWrapperContent>

      {children}
    </div>
  );
};

const ContractSelect = ({
  contractList,
  onValueChange,
  value,
}: {
  contractList?: ListContractsQuery['listContracts']['contracts'];
  onValueChange: (newValue: number | undefined) => void;
  value: number | undefined;
}): JSX.Element => {
  if (!contractList) {
    return (
      <ContractSelectWrapper>
        <Select options={[]} disabled={true} placeholder="No Contracts" onChange={() => {}} />
      </ContractSelectWrapper>
    );
  }

  const formatDate = (dateString: string) => {
    const date = moment(new Date(dateString));
    // to prevent issues with first and last days of month
    const middleOfDay = date.add(12, 'hours');
    return middleOfDay.format('MMM YY');
  };

  const contractOptions = contractList.map(contract => {
    return {
      id: contract.id,
      label: `${formatDate(contract.startDate)} - ${formatDate(contract.endDate)}`,
    };
  });

  return (
    <ContractSelectWrapper>
      <Select
        options={contractOptions}
        onChange={val => {
          if (val == null) {
            onValueChange(undefined);
            return;
          }
          if (typeof val === 'string') {
            onValueChange(parseInt(val, 10));
            return;
          }
          onValueChange(val);
        }}
        value={value}
      />
    </ContractSelectWrapper>
  );
};

const ContractPageWrapperContent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const ContractSelectWrapper = styled.div`
  margin-right: 1rem;
`;

const ContractListAndAddButton = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;
